import React, { useCallback, useEffect, useState } from "react";
import { HardHat, Landmark, Package, AlertTriangle, Info, RefreshCw, CheckCircle2 } from "lucide-react";
import { portalApi } from "../../lib/portalApi";

const Bar = ({ pct, color }) => (
  <div className="h-2 bg-[#6E1F2C]/10 w-full overflow-hidden">
    <div className={`h-full ${color}`} style={{ width: `${Math.max(0, Math.min(100, pct || 0))}%` }} />
  </div>
);

export default function ProgressSummaryCard({ slug, isAdmin, onChanged }) {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState("");

  const load = useCallback(() => {
    portalApi.progressSummary(slug).then(setS).catch(() => null);
  }, [slug]);

  useEffect(() => {
    let on = true;
    portalApi.progressSummary(slug).then((d) => { if (on) setS(d); }).catch(() => null);
    return () => { on = false; };
  }, [slug]);

  const recalc = async () => {
    setBusy("recalc");
    try {
      const r = await portalApi.recalcProgress(slug);
      load();
      if (onChanged) onChanged();
      const pkg = r.external_packages || {};
      window.alert(
        `Overview recalculated from current data.\n` +
        `Overall: ${r.overall_percent ?? "—"}% · Physical: ${r.physical_execution_percent ?? "—"}% · Authority approvals: ${r.authority_approval_percent ?? "—"}%\n` +
        `Client/supplier packages: ${pkg.done ?? 0}/${pkg.count ?? 0} received\n` +
        `Next milestone: ${r.next_milestone ? `${r.next_milestone.name} (${r.next_milestone.target_date || "no date"})` : "—"}\n` +
        `Target completion: ${r.target_completion || "—"}${r.target_updated_from_programme ? " (updated from programme)" : ""}\n\n${r.safeguard}`
      );
    } catch (e) { window.alert(`Recalculation failed: ${e.message}`); }
    setBusy("");
  };

  const completeApprovals = async () => {
    const ok = window.confirm(
      "Mark ALL open authority / third-party approval items as Completed?\n\nOnly approval-type tasks (NOCs, permits, authority coordination) will be updated — site works, financials, daily updates, photos, reports, comments, files and Gantt dates are never touched. Delay notes stay visible as history."
    );
    if (!ok) return;
    setBusy("approvals");
    try {
      const r = await portalApi.markApprovalsComplete(slug);
      load();
      if (onChanged) onChanged();
      window.alert(
        r.updated_count
          ? `${r.updated_count} approval item(s) marked completed.\nAuthority approvals now: ${r.authority_approval_percent}% · Overall: ${r.overall_percent}%\n\n${r.safeguard}`
          : "All approval items were already completed — progress recalculated."
      );
    } catch (e) { window.alert(`Action failed: ${e.message}`); }
    setBusy("");
  };

  if (!s || s.physical_execution_percent === null || s.physical_execution_percent === undefined) return null;
  const cat = s.categories || {};
  const delays = s.delays || {};

  return (
    <div className="bg-white border border-[#6E1F2C]/15 p-6 md:p-7 mb-7" data-testid="portal-progress-summary">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <p className="text-[10px] tracking-[0.32em] uppercase text-[#6E1F2C]">Progress Breakdown</p>
        {isAdmin && (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={recalc}
              disabled={!!busy}
              data-testid="portal-progress-recalc-btn"
              className="inline-flex items-center gap-1.5 border border-[#6E1F2C]/40 text-[#6E1F2C] px-2.5 py-1.5 text-[9px] tracking-[0.24em] uppercase hover:bg-[#6E1F2C] hover:text-white transition-colors disabled:opacity-50"
            >
              <RefreshCw size={11} className={busy === "recalc" ? "animate-spin" : ""} /> Recalculate
            </button>
            <button
              type="button"
              onClick={completeApprovals}
              disabled={!!busy}
              data-testid="portal-approvals-complete-btn"
              className="inline-flex items-center gap-1.5 border border-[#A6822F]/50 text-[#A6822F] px-2.5 py-1.5 text-[9px] tracking-[0.24em] uppercase hover:bg-[#A6822F] hover:text-white transition-colors disabled:opacity-50"
            >
              <CheckCircle2 size={11} /> Mark approvals complete
            </button>
          </div>
        )}
      </div>

      {(s.active_site_phases || []).length > 0 && (
        <div className="mb-5 flex flex-wrap items-center gap-2" data-testid="portal-current-phase-strip">
          <span className="text-[10px] tracking-[0.24em] uppercase text-[#7A6B5F]">Current Site Phase</span>
          {s.active_site_phases.map((a) => (
            <span
              key={a.key}
              className="inline-flex items-center gap-1.5 bg-[#6E1F2C] text-white px-2.5 py-1 text-[11px] font-medium"
              data-testid={`portal-current-phase-${a.key}`}
            >
              {a.percent != null ? `${a.label} — ${a.percent}%` : (a.display || a.label)}
            </span>
          ))}
          <span className="text-[10px] text-[#7A6B5F]">Overall % is weighted across the full fit-out programme.</span>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs text-[#3E2A22] flex items-center gap-1.5"><HardHat size={13} className="text-[#6E1F2C]" /> Physical Execution Progress</span>
            <b className="text-sm text-[#1C110C]" data-testid="portal-physical-progress">{s.physical_execution_percent}%</b>
          </div>
          <Bar pct={s.physical_execution_percent} color="bg-[#6E1F2C]" />
          <p className="text-[10px] text-[#7A6B5F] mt-1.5">
            {cat.contractor_physical?.completed || 0}/{cat.contractor_physical?.count || 0} site tasks complete
            {s.physical_execution_start_date ? ` · site works measured from ${s.physical_execution_start_date}` : ""}
          </p>
        </div>
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs text-[#3E2A22] flex items-center gap-1.5"><Landmark size={13} className="text-[#A6822F]" /> Authority / Third-Party Approvals</span>
            <b className="text-sm text-[#1C110C]" data-testid="portal-authority-progress">{s.authority_approval_percent}%</b>
          </div>
          <Bar pct={s.authority_approval_percent} color="bg-[#A6822F]" />
          <p className="text-[10px] tracking-wide text-[#7A6B5F] mt-1.5">
            Capped at {Math.round((s.authority_weight || 0.05) * 100)}% of overall progress ·{" "}
            {(cat.authority_approval?.count || 0) + (cat.property_management?.count || 0)} approval activities
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-5 text-[11px] text-[#3E2A22]">
        <div className="border border-[#6E1F2C]/10 p-3" data-testid="portal-supplier-status">
          <p className="flex items-center gap-1.5 text-[#7A6B5F] mb-1"><Package size={12} /> Client / Supplier Items</p>
          {s.external_packages?.count ? (
            <>
              <b>{s.external_packages.count}</b> external packages · <b>{s.external_packages.done}</b> received
              {s.external_packages.in_progress ? ` · ${s.external_packages.in_progress} in progress` : ""}
            </>
          ) : (
            <>
              <b>{cat.client_supplier?.count || 0}</b> tracked · {cat.client_supplier?.completed || 0} done
            </>
          )}
        </div>
        <div className="border border-[#6E1F2C]/10 p-3" data-testid="portal-contractor-delays">
          <p className="flex items-center gap-1.5 text-[#7A6B5F] mb-1"><AlertTriangle size={12} /> Contractor Delays</p>
          <b>{(delays.contractor || []).length}</b> attributable
        </div>
        <div className="border border-[#6E1F2C]/10 p-3 col-span-2" data-testid="portal-external-delays">
          <p className="flex items-center gap-1.5 text-[#7A6B5F] mb-1"><Landmark size={12} /> Authority / External Approval Dependencies</p>
          <b>{(delays.external || []).length}</b> external delay item{(delays.external || []).length === 1 ? "" : "s"}
          {(delays.external || []).slice(0, 2).map((d) => (
            <span key={d.id} className="block text-[10px] text-[#7A6B5F] truncate">• {d.name}{d.delay_reason ? ` — ${d.delay_reason}` : ""}</span>
          ))}
        </div>
      </div>

      <div className="mt-5 space-y-2">
        <p className="text-[10px] leading-relaxed text-[#7A6B5F] flex gap-2" data-testid="portal-progress-note">
          <Info size={12} className="shrink-0 mt-0.5 text-[#A6822F]" />{s.notes?.progress_calc}
        </p>
        <p className="text-[10px] leading-relaxed text-[#7A6B5F] flex gap-2" data-testid="portal-approval-note">
          <Info size={12} className="shrink-0 mt-0.5 text-[#A6822F]" />{s.notes?.approval_stage}
        </p>
      </div>
    </div>
  );
}
